import { useState } from 'react';
import { Button } from '@zep-us/zus';
import { ZepColorPrimaryIcon, ZepQuizColorPrimaryIcon, ZepSchoolColorPrimaryIcon } from '@zep-us/zus/icons';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { NoSSR } from '@/components';
import { useLocale } from '@/hooks';
import { ChevronRightIcon } from '@/icons';
import { BestUserIcon } from '@/layout/Header/icons/BestUserIcon';
import { TeacherReviewIcon } from '@/layout/Header/icons/TeacherReviewIcon';
import { MobileDrawer, MobileDrawerLanguageSelector } from '@/layout/Header/MobileDrawer/MobileDrawer';
import { MyMenuDropdown } from '@/layout/Header/MyMenuDropdown';
import { useUser } from '@/lib/auth';
import { FirstPurchaseTooltip } from '@/shared/components/FirstPurchaseTooltip/FirstPurchaseTooltip';
import { GnbLanguageSelector } from '@/shared/components/LanguageSelector';
import { useCountryCode } from '@/shared/hooks';
import { HomeLogo } from '../HomeLogo';
import { getSupportLinks } from './constants/gnbConstants';
import { useSchoolTypeDropdown } from './hooks/useSchoolTypeDropdown';
import { BlogIcon } from './icons/Blog';
import { Event } from './icons/Event';
import { FAQ } from './icons/FAQ';
import { Notice } from './icons/Notice';
import { QuizForBizIcon } from './icons/QuizForBiz';
import { UpdateNews } from './icons/UpdateNews';
import { UserGuide } from './icons/UserGuide';
import { type GnbButtonType } from './GnbButtonType';
import { filterVisibleButtons } from './GnbHeader';
import { HeaderContainer } from './HeaderContainer';
import { LeftButtons } from './LeftButtons';
const PRODUCT_ENTRY_PATH = '/public';
const FREE_START_PRODUCT_ENTRY_PATH = PRODUCT_ENTRY_PATH;
export function PromotionHeader() {
  const {
    t
  } = useTranslation(['common']);
  const {
    locale
  } = useLocale();
  const {
    dropdownButton
  } = useSchoolTypeDropdown();
  const {
    countryCode,
    isReady,
    isKR
  } = useCountryCode();
  const {
    isAuthenticated,
    isInitialized,
    logOut
  } = useUser();
  const isLoggedIn = isInitialized && isAuthenticated;
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const hideKoreaOnlyMenu = locale !== 'ko';
  const showKoreaOnlyMenu = isReady && isKR;
  const supportLinks = getSupportLinks(countryCode);
  const publicDropdownButton: GnbButtonType = {
    ...dropdownButton,
    subButtons: dropdownButton.subButtons?.map(subButton => ({
      ...subButton,
      target: '_blank'
    }))
  };
  const gnbButtons: GnbButtonType[] = [{
    label: t('common.header.about'),
    clickEventname: 'gnb_about_click',
    subButtons: [{
      label: 'ZEP QUIZ',
      icon: <ZepQuizColorPrimaryIcon />,
      href: '/landing',
      clickEventname: 'gnb_about_zep_quiz_click'
    }, {
      label: 'ZEP',
      icon: <ZepColorPrimaryIcon />,
      href: 'https://zep.us/',
      target: '_blank',
      clickEventname: 'gnb_about_zep_click'
    }, {
      label: 'ZEP SCHOOL',
      icon: <ZepSchoolColorPrimaryIcon />,
      href: 'https://zepschool.us/',
      target: '_blank',
      clickEventname: 'gnb_about_zep_school_click'
    }]
  }, publicDropdownButton, {
    label: t('common.header.pricingGuide'),
    href: '/pricing',
    clickEventname: 'gnb_pricing_click',
    wrapper: children => <FirstPurchaseTooltip>{children}</FirstPurchaseTooltip>
  }, {
    label: t('header.useCase'),
    clickEventname: 'gnb_use_case_click',
    subButtons: [{
      label: t('header.useCase.blog'),
      icon: <BlogIcon />,
      href: t('common.quizBlogLink'),
      target: '_blank',
      clickEventname: 'gnb_blog_click',
      sectionTitle: t('header.useCase.educational')
    }, {
      label: '선생님 후기',
      icon: <TeacherReviewIcon />,
      href: 'https://zep-quiz-teachers-review.super.site/',
      target: '_blank',
      clickEventname: 'gnb_review_click',
      isHidden: hideKoreaOnlyMenu
    }, {
      label: '우수 활동자 사례',
      icon: <BestUserIcon />,
      href: 'https://zep-quiz-teachers-review.super.site/%ec%9a%b0%ec%88%98-%ed%99%9c%eb%8f%99%ec%9e%90-%ec%86%8c%ea%b0%9c',
      target: '_blank',
      clickEventname: 'gnb_best_user_click',
      isHidden: hideKoreaOnlyMenu
    }, {
      label: t('header.useCase.quizForBiz'),
      icon: <QuizForBizIcon />,
      href: `${process.env.NEXT_PUBLIC_BASE_URL}/biz`,
      target: '_blank',
      clickEventname: 'gnb_business_click',
      sectionTitle: t('header.useCase.corporate')
    }]
  }, {
    label: t('common.header.customerSupport'),
    clickEventname: 'gnb_customer_support_click',
    subButtons: [{
      label: t('home.pricing.frequentlyQuestion'),
      icon: <FAQ />,
      href: supportLinks.faq,
      target: '_blank',
      clickEventname: 'gnb_faq_click'
    }, {
      label: t('common.header.userGuide'),
      icon: <UserGuide />,
      href: supportLinks.guide,
      target: '_blank',
      clickEventname: 'gnb_guide_click'
    }, {
      label: t('common.header.updateNews'),
      icon: <UpdateNews />,
      href: supportLinks.updateNews,
      target: '_blank',
      clickEventname: 'gnb_update_news_click'
    }, {
      label: t('gnb.header.event'),
      icon: <Event />,
      href: supportLinks.event,
      target: '_blank',
      clickEventname: 'gnb_event_click',
      isHidden: !showKoreaOnlyMenu
    }, {
      label: t('common.header.notice'),
      icon: <Notice />,
      href: supportLinks.notice,
      target: '_blank',
      clickEventname: 'gnb_notice_click'
    }]
  }];
  const renderStartButton = ({
    compact = false,
    showRightIcon = true
  }: {
    compact?: boolean;
    showRightIcon?: boolean;
  } = {}) => <Button size={compact ? 'sm' : 'xs'} intent="strong" className={compact ? 'h-[30px] min-h-[30px] rounded-xs px-xs text-caption-lg-semibold' : undefined} rightSlot={showRightIcon ? <ChevronRightIcon size="xs" fill={ICON_COLOR.inverse} /> : undefined} onClick={() => {
    logger.track('enter_public', {
      location: 'landing_header_start_free'
    });
    window.open(FREE_START_PRODUCT_ENTRY_PATH, '_blank', 'noopener,noreferrer');
  }} data-sentry-element="Button" data-sentry-component="renderStartButton" data-sentry-source-file="PromotionHeader.tsx">
      {t('common:pricing.free.startForFree')}
    </Button>;
  const renderLoginButton = ({
    compact = false
  }: {
    compact?: boolean;
  } = {}) => <Button size={compact ? 'sm' : 'xs'} variant="transparent" intent="neutral" className={compact ? 'h-[30px] min-h-[30px] rounded-xs px-xs text-caption-lg-semibold' : undefined} asChild data-sentry-element="Button" data-sentry-component="renderLoginButton" data-sentry-source-file="PromotionHeader.tsx">
      <Link href={{
      pathname: '/login',
      query: {
        callbackUrl: PRODUCT_ENTRY_PATH
      }
    }} onClick={() => {
      logger.track('enter_login', {
        location: 'landing_header'
      });
    }} prefetch={false} data-sentry-element="Link" data-sentry-source-file="PromotionHeader.tsx">
        {t('header.login')}
      </Link>
    </Button>;
  return <HeaderContainer variant="compact" className="h-[50px] min-h-[50px] justify-start border-b border-alternative py-[12px] tablet-sm:h-auto tablet-sm:border-b-0" data-sentry-element="HeaderContainer" data-sentry-component="PromotionHeader" data-sentry-source-file="PromotionHeader.tsx">
      <div className="flex shrink-0 items-center gap-3xs">
        <HomeLogo location="header" redirectUrl="/landing" className="!h-[28px] !min-h-[28px] !w-[28px] !min-w-[28px] tablet-sm:!h-[28px] tablet-sm:!min-h-[28px] tablet-sm:!w-[128.8px] tablet-sm:!min-w-[128.8px]" data-sentry-element="HomeLogo" data-sentry-source-file="PromotionHeader.tsx" />
      </div>
      <div className="ml-[16px] hidden items-center gap-2xs overflow-x-auto tablet-sm:flex [&>*]:shrink-0">
        <LeftButtons buttons={filterVisibleButtons(gnbButtons)} size="xs" data-sentry-element="LeftButtons" data-sentry-source-file="PromotionHeader.tsx" />
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2xs">
        <div className="flex items-center gap-xs tablet-sm:hidden">
          {!isMobileDrawerOpen && <>
              {renderStartButton({
            compact: true,
            showRightIcon: false
          })}
              {!isLoggedIn && renderLoginButton({
            compact: true
          })}
            </>}
          <NoSSR fallback={<MobileDrawer.TriggerContent />} data-sentry-element="NoSSR" data-sentry-source-file="PromotionHeader.tsx">
            <MobileDrawer buttons={filterVisibleButtons(gnbButtons)} bodyBottomAccessory={<MobileDrawerLanguageSelector />} footerBottomAccessory={<>
                  <Button className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" size="sm" intent="strong" asChild>
                    <Link href={FREE_START_PRODUCT_ENTRY_PATH} target="_blank" onClick={() => {
                logger.track('enter_public', {
                  location: 'landing_header_start_free'
                });
              }} prefetch={false}>
                      {t('common:pricing.free.startForFree')}
                    </Link>
                  </Button>
                  {isLoggedIn ? <Button className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" size="sm" variant="transparent" intent="neutral" onClick={() => {
              void logOut({
                redirectTo: '/landing'
              });
            }}>
                      {t('common:header.logout')}
                    </Button> : <Button className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" size="sm" variant="contained" intent="alternative" asChild>
                      <Link href={{
                pathname: '/login',
                query: {
                  callbackUrl: PRODUCT_ENTRY_PATH
                }
              }} onClick={() => {
                logger.track('enter_login', {
                  location: 'landing_header'
                });
              }} prefetch={false}>
                        {t('header.login')}
                      </Link>
                    </Button>}
                </>} onOpenChange={setIsMobileDrawerOpen} data-sentry-element="MobileDrawer" data-sentry-source-file="PromotionHeader.tsx" />
          </NoSSR>
        </div>
        <div className="hidden gap-xs tablet-sm:flex tablet-sm:items-center">
          {renderStartButton({
          showRightIcon: !isLoggedIn
        })}
          {!isLoggedIn && renderLoginButton()}
          <GnbLanguageSelector size="xs" iconSize="xs" data-sentry-element="GnbLanguageSelector" data-sentry-source-file="PromotionHeader.tsx" />
          {isLoggedIn && <MyMenuDropdown triggerSize={32} />}
        </div>
      </div>
    </HeaderContainer>;
}